import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { courseAPI } from '../lib/studentAPI';
import type { Course } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { Clock, Rocket, Lock, Users, Award, Zap } from 'lucide-react';
import { toast } from 'sonner';

export const LandingPage = () => {
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);

  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    loadCourses();
  }, []);

  const loadCourses = async () => {
    try {
      const data = await courseAPI.getPublishedCourses();
      setCourses(data);
    } catch (error) {
      console.error('Failed to load courses:', error);
      toast.error('Failed to load courses. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleEnrollClick = useCallback((courseId: string, isLocked: boolean) => {
    if (isLocked) return;

    if (!isAuthenticated) {
      toast.info("Login to enroll and start learning");
      navigate('/login', { state: { courseId } });
      return;
    }

    navigate('/dashboard', { state: { enrollCourseId: courseId } });
  }, [isAuthenticated, navigate]);

  return (
    <div className="min-h-screen bg-zinc-50 overflow-hidden">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-50">
        <div className="mx-auto max-w-7xl px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {/* Google-inspired Logo */}
            <div className="h-12 w-12 rounded-2xl bg-gradient-to-br from-amber-500 to-amber-700 shadow-lg flex items-center justify-center overflow-hidden">
              <svg viewBox="0 0 100 100" className="w-9 h-9">
                {/* Lion body */}
                <circle cx="50" cy="55" r="20" fill="#D4A017" />
                
                {/* Lion head */}
                <circle cx="50" cy="35" r="18" fill="#F5A623" />
                
                {/* Mane */}
                <circle cx="50" cy="35" r="23" fill="none" stroke="#D4A017" strokeWidth="4" strokeDasharray="4 3" />
                
                {/* Ears */}
                <circle cx="35" cy="22" r="5" fill="#D4A017" />
                <circle cx="65" cy="22" r="5" fill="#D4A017" />
                
                {/* Face */}
                <circle cx="42" cy="33" r="2.5" fill="#333" />
                <circle cx="58" cy="33" r="2.5" fill="#333" />
                <path d="M44 42 Q50 48 56 42" stroke="#333" strokeWidth="2" fill="none" />
                
                {/* Nose */}
                <ellipse cx="50" cy="39" rx="3" ry="2" fill="#8B4513" />
                
                {/* Book held by paws */}
                <rect x="38" y="58" width="24" height="14" rx="1" fill="#fff" stroke="#333" strokeWidth="1" />
                <line x1="50" y1="58" x2="50" y2="72" stroke="#333" strokeWidth="0.8" />
                <text x="43" y="68" fontSize="6" fill="#333" fontWeight="bold">A</text>
                
                {/* Paws */}
                <ellipse cx="38" cy="64" rx="4" ry="3" fill="#F5A623" />
                <ellipse cx="62" cy="64" rx="4" ry="3" fill="#F5A623" />
              </svg>
            </div>
            <div>
              <h1 className="font-bold text-2xl tracking-tight">AQL</h1>
              <p className="text-xs text-gray-500 -mt-1">Master Skills. Build Breakthroughs.</p>
            </div>
           
          </div>
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/login')}>
              Login
            </Button>
            <Button onClick={() => navigate('/register')} className="bg-violet-600 hover:bg-violet-700">
              Join Free
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section - More Vibrant */}
      <section className="relative pt-20 pb-24 bg-gradient-to-br from-violet-600 via-fuchsia-600 to-rose-500 text-white overflow-hidden">
        {/* Background Decorations */}
        <div className="absolute inset-0 bg-[radial-gradient(at_30%_20%,rgba(255,255,255,0.15)_0%,transparent_50%)]" />
        <div className="absolute -top-20 -right-20 w-96 h-96 bg-white/10 rounded-full blur-3xl" />
        <div className="absolute -bottom-32 -left-20 w-96 h-96 bg-white/10 rounded-full blur-3xl" />

        <div className="mx-auto max-w-7xl px-6 relative z-10">
          <div className="max-w-3xl">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
            >
              <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-md px-4 py-1.5 rounded-full text-sm mb-6">
                <Zap className="w-4 h-4" /> ENJOY EASY UPSKILL WITH AQL
              </div>

              <h1 className="text-5xl lg:text-6xl font-bold tracking-tighter leading-tight mb-6">
                Learn Today.<br />
                Lead Tomorrow.
              </h1>

              <p className="text-xl text-white/90 mb-10">
                Join thousands of learners mastering in-demand skills with expert instructors and interactive courses.
              </p>

              <div className="flex flex-wrap gap-4">
                <Button 
                  size="lg" 
                  onClick={() => navigate('/register')}
                  className="bg-white text-violet-700 hover:bg-gray-100 text-lg px-10 py-7 rounded-2xl font-semibold"
                >
                  Start Learning Free
                </Button>
                <Button 
                  size="lg" 
                  variant="outline" 
                  onClick={() => document.getElementById('courses')?.scrollIntoView({ behavior: 'smooth' })}
                  className="border-white text-white hover:bg-white/10 text-lg px-10 py-7 rounded-2xl"
                >
                  Browse Courses
                </Button>
              </div>

              <div className="mt-10 flex items-center gap-8 text-sm">
                <div>⭐⭐⭐⭐⭐ YOUR EXAM IS INNOVATION HACKTHON. </div>
                <div className="flex -space-x-3">
                  {[1,2,3].map(i => (
                    <div key={i} className="w-8 h-8 rounded-full border-2 border-white bg-zinc-300" />
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="mx-auto max-w-7xl px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold tracking-tight mb-4">Why Learners Love AQL</h2>
            <p className="text-gray-600 text-lg">Everything you need to succeed in one platform,the best innovators are rewarded</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: <Zap className="w-10 h-10 text-violet-600" />,
                title: "Learn at Your Pace",
                desc: "Flexible self-paced courses with lifetime access"
              },
              {
                icon: <Users className="w-10 h-10 text-fuchsia-600" />,
                title: "Expert Instructors",
                desc: "Learn directly from industry leaders and professionals"
              },
              {
                icon: <Award className="w-10 h-10 text-rose-600" />,
                title: "Verified Certificates",
                desc: "Earn shareable certificates recognized by employers"
              },
            ].map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-zinc-50 border border-zinc-100 rounded-3xl p-10 hover:shadow-xl transition-all group"
              >
                <div className="mb-6">{feature.icon}</div>
                <h3 className="text-2xl font-semibold mb-3">{feature.title}</h3>
                <p className="text-gray-600 leading-relaxed">{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Course Catalogue */}
      <section id="courses" className="py-20 bg-zinc-100">
        <div className="mx-auto max-w-7xl px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold tracking-tight mb-3">Featured Courses</h2>
            <p className="text-gray-600 text-lg">Start your learning journey with these top-rated programs</p>
          </div>

          {loading ? (
            // Skeleton loaders...
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1,2,3].map(i => (
                <div key={i} className="bg-white rounded-3xl overflow-hidden animate-pulse h-[420px]" />
              ))}
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {courses.map((course, index) => (
                <motion.div
                  key={course.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.08 }}
                  className="group bg-white rounded-3xl overflow-hidden shadow-sm hover:shadow-2xl transition-all duration-300 border border-zinc-100"
                >
                  <div className="relative h-56 overflow-hidden">
                    {course.coverImage ? (
                      <img
                        src={course.coverImage}
                        alt={course.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-violet-500 to-fuchsia-600 flex items-center justify-center text-7xl">
                        📖
                      </div>
                    )}
                    <div className="absolute top-4 right-4">
                      <Badge variant={course.isLocked ? "secondary" : "default"} className="shadow">
                        {course.isLocked ? <Lock className="mr-1 h-3 w-3" /> : <Rocket className="mr-1 h-3 w-3" />}
                        {course.isLocked ? "Coming Soon" : "Available"}
                      </Badge>
                    </div>
                  </div>

                  <div className="p-7">
                    <h3 className="font-semibold text-xl mb-2 line-clamp-2">{course.title}</h3>
                    {course.subtitle && <p className="text-gray-600 mb-4 line-clamp-2">{course.subtitle}</p>}

                    {course.timeline && (
                      <div className="flex items-center gap-2 text-sm text-gray-500 mb-5">
                        <Clock className="h-4 w-4" />
                        {course.timeline}
                      </div>
                    )}

                    <Button
                      className="w-full py-6 text-base rounded-2xl"
                      onClick={() => handleEnrollClick(course.id, !!course.isLocked)}
                      disabled={course.isLocked}
                    >
                      {course.isLocked 
                        ? "Coming Soon" 
                        : isAuthenticated 
                        ? "Start Course" 
                        : "Login to Enroll"}
                    </Button>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

            {/* Footer */}
      <footer className="bg-zinc-900 text-gray-300 py-16">
        <div className="mx-auto max-w-7xl px-6">
          <div className="grid md:grid-cols-4 gap-10">
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="h-9 w-9 rounded-2xl bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center">
                  <span className="font-bold text-white">LP</span>
                </div>
                <span className="font-bold text-white text-xl">AQL</span>
              </div>
              <p className="text-sm">Making quality education accessible to everyone in world.</p>
            </div>

            <div>
              <h4 className="font-semibold text-white mb-4">Contact</h4>
              <ul className="space-y-2 text-sm">
                <li>info.aqlonline@gmail.com</li>
                <li>+254 0142379030</li>
                <li>
                  <a 
                    href="https://wa.me/2540142379030" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 hover:text-green-400 transition-colors"
                  >
                    <span>💬</span> WhatsApp
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold text-white mb-4">Success Stories</h4>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white font-bold">JM</div>
                  <div>
                    <p className="text-sm font-medium text-white">James Abam</p>
                    <p className="text-xs text-gray-400">"From zero to full-stack dev in 6 months"</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center text-white font-bold">SK</div>
                  <div>
                    <p className="text-sm font-medium text-white">Sarah Karimi</p>
                    <p className="text-xs text-gray-400">"Landed my dream job"</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-green-400 to-teal-500 flex items-center justify-center text-white font-bold">JO</div>
                  <div>
                    <p className="text-sm font-medium text-white">John Mukasa</p>
                    <p className="text-xs text-gray-400">"Started my own tech agency"</p>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h4 className="font-semibold text-white mb-4">Quick Links</h4>
              <ul className="space-y-2 text-sm">
                <li className="cursor-pointer hover:text-blue-400">Browse Courses</li>
                <li className="cursor-pointer hover:text-blue-400">For Businesses</li>
              </ul>
            </div>
          </div>

          <div className="border-t border-zinc-800 mt-12 pt-8 text-center text-sm">
            ©AQL. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};
